import { CirclePlay } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  ClientDataModal,
  ClientDuplicationDialog,
  DeleteClientDialog,
  LinkClientPartnerDialog,
  SendResultsLinkModal,
  cn,
  fetchClientsForAdvisorByAdvisorIdRequest,
  updateClientsSearchText,
} from '..';
import { PlayIcon } from '../components/PlayIcon';
import {
  selectAdvisorHierarchyState,
  selectClients,
  selectClientsIsLoading,
  selectClientsSearchText,
  selectSessionAdvisor,
} from '../model/selectors';

import { ColumnDef } from '@tanstack/react-table';
import { Send } from 'lucide-react';
import { createClientTableColumns } from '../components/ClientsTable';
import { DataTable } from '../components/DataTable';
import { Button } from '../components/ui/button';
import { useIsMobile } from '@client/hooks/use-mobile';
import { DashboardCardView } from '@/components/advisor-mobile-dashboard';
import { useFeatureFlags } from '@client/contexts/FeatureFlagsContext';
import { useInviteClientTrigger } from '@client/components/invite-client/InviteClientContext';

type ModalType =
  | 'delete'
  | 'linkPartner'
  | 'sendClientSigninLink'
  | 'clientData'
  | 'newDuplicateClient'
  | null;
export interface ModalState {
  type: ModalType;
  client?: Client; // Could be typed more specifically based on your needs
}

const CLIENTS_VIDEO_URL = 'https://www.loom.com/embed/90255565dc404610846adecbd7d82049';

export function AdvisorDashboardClients() {
  const dispatch = useDispatch();
  const advisor = useSelector(selectSessionAdvisor);
  const clients = useSelector(selectClients);
  const clientsIsLoading = useSelector(selectClientsIsLoading);
  const { subordinateAdvisors } = useSelector(selectAdvisorHierarchyState);
  const [searchText, setSearchText] = useState(useSelector(selectClientsSearchText));
  const { setInviteClientParams } = useInviteClientTrigger();

  const handleSearchTextChange = (searchText: string) => {
    setSearchText(searchText);
    dispatch(updateClientsSearchText(searchText));
    localStorage.setItem('clientsSearchText', searchText);
  };

  const [modalState, setModalState] = useState<ModalState>({ type: null });
  const isMobile = useIsMobile();
  const onCloseModal = () => setModalState({ type: null });
  const { flags } = useFeatureFlags();

  // Memoize the columns creation since it depends on clients and setModalState
  const memoizedColumns = useMemo(
    () =>
      createClientTableColumns({
        data: clients,
        setModalState,
        sessionAdvisor: advisor,
        subordinateAdvisors: subordinateAdvisors || ([] as Advisor[]),
        dispatch,
      }),
    [clients, setModalState, advisor, subordinateAdvisors],
  );

  const memoizedHandleRefresh = useCallback(() => {
    dispatch(
      fetchClientsForAdvisorByAdvisorIdRequest({
        advisorId: advisor.advisorId,
        isInvalidation: false,
      }),
    );
  }, [advisor.advisorId, dispatch]);

  // Memoize the entire DataTable instance
  const memoizedDataTable = useMemo(
    () => (
      <DataTable<Client>
        columns={memoizedColumns as ColumnDef<Client>[]}
        getRowId={row => row.clientId}
        data={clients}
        isLoading={clientsIsLoading}
        globalSearchFilterKey="Client"
        globalSearchText={searchText ?? undefined}
        globalSearchFieldKeys={['clientName', 'email']}
        handleRefresh={memoizedHandleRefresh}
        className="mb-12"
        tableId="clientsTable"
        variant="outlined"
        initialColumnVisibility={{
          organization: Boolean(subordinateAdvisors && subordinateAdvisors.length > 0),
        }}
        initialColumnPinning={{
          left: ['clientName'],
          right: [],
        }}
        handleSearchTextChange={handleSearchTextChange}
        tableSkeletonRows={10}
        initialSorting={[{ id: 'addedDate', desc: true }]}
      />
    ),
    [
      memoizedColumns,
      clients,
      clientsIsLoading,
      searchText,
      memoizedHandleRefresh,
      subordinateAdvisors,
    ],
  );

  return (
    <div className="h-full">
      {flags.mobileDashboardRedesign ? (
        <main className={cn(isMobile ? 'px-0 pt-2' : 'px-4 pb-12 pt-3 sm:px-6 lg:px-8')}>
          <div
            className={cn(
              'flex items-center justify-between',
              isMobile ? 'mb-2 px-4' : 'my-4 px-1',
            )}
          >
            <div className="flex items-center gap-3">
              <h1 className="text-primary-dark text-xl font-semibold md:text-4xl">Clients</h1>
              <PlayIcon
                id="clients-tab-demo-video"
                icon={CirclePlay}
                videoUrl={CLIENTS_VIDEO_URL}
              />
            </div>

            {!isMobile && (
              <Button
                size="lg"
                data-testid="invite-client-button-test"
                onClick={() => setInviteClientParams()}
                className="bg-primary hover:bg-primary-hover rounded-full py-6 text-base text-white"
              >
                <Send className="size-4" />
                Invite Clients
              </Button>
            )}
          </div>

          {!isMobile && <div className="bg-primary-100 my-6 h-px w-full" />}

          <div className={isMobile ? '' : 'my-4'}>
            {isMobile ? (
              <DashboardCardView
                clients={clients}
                setModalState={setModalState}
                sessionAdvisor={advisor}
                subordinateAdvisors={subordinateAdvisors || []}
                isLoading={clientsIsLoading}
                handleRefresh={memoizedHandleRefresh}
                globalSearchText={searchText ?? undefined}
                handleSearchTextChange={handleSearchTextChange}
              />
            ) : (
              memoizedDataTable
            )}
          </div>
        </main>
      ) : (
        <main className="px-4 pb-12 pt-3 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="my-7 flex flex-col items-center justify-between gap-8 md:flex-row md:gap-0">
            <div className="flex items-center gap-5">
              <h1 className="text-primary-dark text-4xl font-semibold">Clients</h1>
              <PlayIcon
                id="clients-tab-demo-video"
                icon={CirclePlay}
                videoUrl={CLIENTS_VIDEO_URL}
              />
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="lg"
                data-testid="invite-client-button-test"
                onClick={() => setInviteClientParams()}
                className="bg-primary hover:bg-primary-hover rounded-full py-6 text-base text-white"
              >
                <Send className="h-7 w-7" />
                Invite Clients
              </Button>
            </div>
          </div>
          {/* divider */}
          <div className="bg-primary-100 my-6 h-px w-full" />
          {memoizedDataTable}
        </main>
      )}
      {/* Modals/Dialogs */}
      {modalState.client && (
        <>
          <DeleteClientDialog
            client={modalState.client}
            open={modalState.type === 'delete'}
            onClose={onCloseModal}
          />
          <LinkClientPartnerDialog
            client={modalState.client}
            open={modalState.type === 'linkPartner'}
            onClose={onCloseModal}
          />
          <SendResultsLinkModal
            client={modalState.client}
            open={modalState.type === 'sendClientSigninLink'}
            onClose={onCloseModal}
          />
          <ClientDataModal
            partialClient={modalState.client}
            open={modalState.type === 'clientData'}
            onClose={onCloseModal}
          />
          <ClientDuplicationDialog
            client={modalState.client}
            open={modalState.type === 'newDuplicateClient'}
            onClose={onCloseModal}
          />
        </>
      )}
    </div>
  );
}
